import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Save, Upload, Puzzle, Search, MessageSquare, Wrench, AlertTriangle } from "lucide-react";

export default function ModSettings() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const fileRef = useRef(null);
    const logoFileRef = useRef(null);
    const plateLogoFileRef = useRef(null);
    const plateFontFileRef = useRef(null);
    const heroFileRef = useRef(null);
    const notFoundFileRef = useRef(null);
    const serverErrorFileRef = useRef(null);
    const ogImageFileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/mod-info");
            setData(res.data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Live-register the admin's uploaded plate font under a private family
    // name so the "SL 12345" preview in this panel already renders with
    // the actual glyphs - no need to visit /generator-tablic to confirm.
    useEffect(() => {
        const url = data?.plate_font_url;
        if (!url || typeof document === "undefined" || !document.fonts) return;
        const absUrl = url.startsWith("http")
            ? url
            : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
        let face;
        try {
            face = new FontFace("AdminPlateFontPreview", `url(${absUrl})`);
        } catch {
            return;
        }
        let cancelled = false;
        face.load()
            .then((loaded) => {
                if (cancelled) return;
                document.fonts.add(loaded);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            try {
                document.fonts.delete(face);
            } catch {
                /* face may not have been added */
            }
        };
    }, [data?.plate_font_url]);

    const onChange = (k) => (e) => {
        const v = e.target.value;
        setData((prev) => ({ ...prev, [k]: v }));
    };

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            let payload = { ...data };
            const file = fileRef.current?.files?.[0];
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.download_url = up.data.url;
                payload.download_type = "file";
                const sizeMB = up.data.size / (1024 * 1024);
                payload.file_size =
                    sizeMB >= 1
                        ? `${sizeMB.toFixed(1)} MB`
                        : `${(up.data.size / 1024).toFixed(0)} KB`;
            }
            const logoFile = logoFileRef.current?.files?.[0];
            if (logoFile) {
                const fd = new FormData();
                fd.append("file", logoFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.logo_url = up.data.url;
            }
            const plateLogoFile = plateLogoFileRef.current?.files?.[0];
            if (plateLogoFile) {
                const fd = new FormData();
                fd.append("file", plateLogoFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.plate_logo_url = up.data.url;
            }
            const plateFontFile = plateFontFileRef.current?.files?.[0];
            if (plateFontFile) {
                const fd = new FormData();
                fd.append("file", plateFontFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.plate_font_url = up.data.url;
            }
            const heroFile = heroFileRef.current?.files?.[0];
            if (heroFile) {
                const fd = new FormData();
                fd.append("file", heroFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.hero_image_url = up.data.url;
            }
            const notFoundFile = notFoundFileRef.current?.files?.[0];
            if (notFoundFile) {
                const fd = new FormData();
                fd.append("file", notFoundFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.not_found_image_url = up.data.url;
            }
            const serverErrorFile = serverErrorFileRef.current?.files?.[0];
            if (serverErrorFile) {
                const fd = new FormData();
                fd.append("file", serverErrorFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.server_error_image_url = up.data.url;
            }
            const ogImageFile = ogImageFileRef.current?.files?.[0];
            if (ogImageFile) {
                const fd = new FormData();
                fd.append("file", ogImageFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.og_image_url = up.data.url;
            }
            const { data: updated } = await api.put("/mod-info", payload);
            setData(updated);
            setSuccess("Zapisano.");
            if (fileRef.current) fileRef.current.value = "";
            if (logoFileRef.current) logoFileRef.current.value = "";
            if (plateLogoFileRef.current) plateLogoFileRef.current.value = "";
            if (plateFontFileRef.current) plateFontFileRef.current.value = "";
            if (heroFileRef.current) heroFileRef.current.value = "";
            if (notFoundFileRef.current) notFoundFileRef.current.value = "";
            if (serverErrorFileRef.current) serverErrorFileRef.current.value = "";
            if (ogImageFileRef.current) ogImageFileRef.current.value = "";
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading || !data) {
        return (
            <div className="text-[color:var(--text-secondary)]">
                <Loader2 className="animate-spin inline" /> Ładowanie...
            </div>
        );
    }

    return (
        <form
            onSubmit={save}
            data-testid="admin-settings"
            className="grid lg:grid-cols-2 gap-6"
        >
            <div className="panel-yellow p-6 space-y-4">
                <div className="label-eyebrow">Informacje</div>
                <Field label="Opis (na hero)">
                    <textarea
                        data-testid="settings-description"
                        value={data.description || ""}
                        onChange={onChange("description")}
                        rows={3}
                        className="input"
                    />
                </Field>
                <Field label={`Tekst „O projekcie"`}>
                    <textarea
                        data-testid="settings-about-text"
                        value={data.about_text || ""}
                        onChange={onChange("about_text")}
                        rows={5}
                        className="input"
                    />
                </Field>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Logo (URL lub plik)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.logo_url && (
                            <img
                                src={
                                    data.logo_url.startsWith("http")
                                        ? data.logo_url
                                        : `${process.env.REACT_APP_BACKEND_URL}${data.logo_url}`
                                }
                                alt="Logo"
                                className="h-12 w-auto object-contain bg-[color:var(--bg-base)] border border-white/10 rounded-md p-1"
                            />
                        )}
                        <input
                            data-testid="settings-logo-url"
                            value={data.logo_url || ""}
                            onChange={onChange("logo_url")}
                            placeholder="https://... lub /api/uploads/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={logoFileRef}
                        data-testid="settings-logo-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-4"
                    />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Logo tablicy rejestracyjnej (URL lub plik)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.plate_logo_url && (
                            <img
                                src={
                                    data.plate_logo_url.startsWith("http")
                                        ? data.plate_logo_url
                                        : `${process.env.REACT_APP_BACKEND_URL}${data.plate_logo_url}`
                                }
                                alt="Logo tablicy"
                                className="h-12 w-12 object-contain bg-white border border-white/10 rounded-md p-1"
                            />
                        )}
                        <input
                            data-testid="settings-plate-logo-url"
                            value={data.plate_logo_url || ""}
                            onChange={onChange("plate_logo_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={plateLogoFileRef}
                        data-testid="settings-plate-logo-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-1"
                    />
                    <p className="text-[10px] text-[color:var(--text-muted)]">
                        Herb/pieczęć wyświetlana na tablicy rejestracyjnej (generator tablic). Zalecany kwadratowy PNG z przezroczystym tłem.
                    </p>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Czcionka tablicy (URL lub plik .ttf / .otf / .woff / .woff2)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.plate_font_url && (
                            <span
                                data-testid="settings-plate-font-preview"
                                className="inline-flex items-center justify-center h-12 min-w-[92px] px-3 rounded-md border border-white/10 bg-white text-black tabular-nums"
                                style={{
                                    fontFamily: '"AdminPlateFontPreview", "License Plate", "Anton", "Impact", sans-serif',
                                    fontWeight: 900,
                                    letterSpacing: "0.06em",
                                    fontSize: "22px",
                                }}
                                title="Podgląd wgranej czcionki"
                            >
                                SL 12345
                            </span>
                        )}
                        <input
                            data-testid="settings-plate-font-url"
                            value={data.plate_font_url || ""}
                            onChange={onChange("plate_font_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept=".ttf,.otf,.woff,.woff2,font/*,application/font-woff,application/font-woff2,application/x-font-ttf,application/x-font-opentype"
                        ref={plateFontFileRef}
                        data-testid="settings-plate-font-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-1"
                    />
                    <p className="text-[10px] text-[color:var(--text-muted)]">
                        Wgraj czcionkę tablic rejestracyjnych (np. Arkisz, GL-Nummernschild-Eng lub inną tablicową w .woff2 dla najlepszej wydajności). Jeśli puste - użyta zostanie awaryjna czcionka {'"License Plate"'} z Google Fonts.
                    </p>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Tło sekcji Hero (URL lub plik)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.hero_image_url && (
                            <img
                                src={
                                    data.hero_image_url.startsWith("http")
                                        ? data.hero_image_url
                                        : `${process.env.REACT_APP_BACKEND_URL}${data.hero_image_url}`
                                }
                                alt="Tło Hero"
                                className="h-12 w-20 object-cover bg-[color:var(--bg-base)] border border-white/10 rounded-md"
                            />
                        )}
                        <input
                            data-testid="settings-hero-url"
                            value={data.hero_image_url || ""}
                            onChange={onChange("hero_image_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={heroFileRef}
                        data-testid="settings-hero-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-4"
                    />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Obrazek strony 404 (URL lub plik)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.not_found_image_url && (
                            <img
                                src={
                                    data.not_found_image_url.startsWith("http")
                                        ? data.not_found_image_url
                                        : `${process.env.REACT_APP_BACKEND_URL}${data.not_found_image_url}`
                                }
                                alt="Obrazek 404"
                                className="h-14 w-14 object-contain bg-[color:var(--bg-base)] border border-white/10 rounded-md p-1"
                            />
                        )}
                        <input
                            data-testid="settings-notfound-url"
                            value={data.not_found_image_url || ""}
                            onChange={onChange("not_found_image_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={notFoundFileRef}
                        data-testid="settings-notfound-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-1"
                    />
                    <p className="text-[10px] text-[color:var(--text-muted)]">
                        Pokazywany obok „404”. Gdy puste - używany jest domyślny znak.
                    </p>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Obrazek strony 500 (URL lub plik)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.server_error_image_url && (
                            <img
                                src={
                                    data.server_error_image_url.startsWith("http")
                                        ? data.server_error_image_url
                                        : `${process.env.REACT_APP_BACKEND_URL}${data.server_error_image_url}`
                                }
                                alt="Obrazek 500"
                                className="h-14 w-14 object-contain bg-[color:var(--bg-base)] border border-white/10 rounded-md p-1"
                            />
                        )}
                        <input
                            data-testid="settings-servererror-url"
                            value={data.server_error_image_url || ""}
                            onChange={onChange("server_error_image_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={serverErrorFileRef}
                        data-testid="settings-servererror-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-1"
                    />
                    <p className="text-[10px] text-[color:var(--text-muted)]">
                        Pokazywany obok „500”. Gdy puste - używany jest domyślny trójkątny znak ostrzegawczy.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Wersja">
                        <input
                            data-testid="settings-version"
                            value={data.version || ""}
                            onChange={onChange("version")}
                            className="input"
                        />
                    </Field>
                    <Field label="Rozmiar pliku">
                        <input
                            data-testid="settings-filesize"
                            value={data.file_size || ""}
                            onChange={onChange("file_size")}
                            className="input"
                        />
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Dzielnica(-e)">
                        <input
                            data-testid="settings-districts"
                            value={data.districts || ""}
                            onChange={onChange("districts")}
                            className="input"
                        />
                    </Field>
                    <Field label="Drogi (km)">
                        <input
                            data-testid="settings-roads"
                            value={data.roads_km || ""}
                            onChange={onChange("roads_km")}
                            className="input"
                        />
                    </Field>
                </div>
                <Field label="Wymagania">
                    <textarea
                        data-testid="settings-requirements"
                        value={data.requirements || ""}
                        onChange={onChange("requirements")}
                        rows={3}
                        className="input"
                    />
                </Field>
                <Field label="Changelog">
                    <textarea
                        data-testid="settings-changelog"
                        value={data.changelog || ""}
                        onChange={onChange("changelog")}
                        rows={5}
                        className="input font-mono text-xs"
                    />
                </Field>
            </div>

            <div className="panel-yellow p-6 space-y-4">
                <div className="label-eyebrow">Plik / link do pobrania</div>
                <Field label="Typ">
                    <select
                        data-testid="settings-download-type"
                        value={data.download_type || "external"}
                        onChange={onChange("download_type")}
                        className="input"
                    >
                        <option value="external">Link zewnętrzny</option>
                        <option value="file">Plik na serwerze</option>
                    </select>
                </Field>
                <Field label="URL / ścieżka">
                    <input
                        data-testid="settings-download-url"
                        value={data.download_url || ""}
                        onChange={onChange("download_url")}
                        placeholder="https://... lub /api/uploads/..."
                        className="input"
                    />
                </Field>
                <Field label="Prześlij plik moda (zastąpi link)">
                    <input
                        type="file"
                        ref={fileRef}
                        data-testid="settings-download-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                    />
                </Field>

                {error && (
                    <div className="text-sm text-red-400" data-testid="settings-error">
                        {error}
                    </div>
                )}
                {success && (
                    <div
                        className="text-sm text-[color:var(--primary)]"
                        data-testid="settings-success"
                    >
                        {success}
                    </div>
                )}

                <button
                    data-testid="settings-save-btn"
                    type="submit"
                    disabled={saving}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {saving ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <Save size={14} />
                    )}
                    Zapisz ustawienia
                </button>
            </div>

            <div className="panel-yellow p-6 lg:col-span-2 space-y-4">
                <div className="label-eyebrow flex items-center gap-2">
                    <Search size={14} /> SEO &amp; udostępnianie
                </div>
                <p className="text-xs text-[color:var(--text-muted)] -mt-2">
                    Pola wykorzystywane do indeksowania w Google oraz podglądu strony w social media (Open Graph / Twitter). Zmiany aplikują się natychmiast po zapisaniu.
                </p>

                <Field label="Adres strony (canonical URL)">
                    <input
                        data-testid="settings-seo-site-url"
                        value={data.seo_site_url || ""}
                        onChange={onChange("seo_site_url")}
                        placeholder="https://projectrs.pl"
                        className="input"
                    />
                </Field>

                <Field label="Tytuł strony (≤ 60 znaków)">
                    <input
                        data-testid="settings-seo-title"
                        value={data.seo_site_title || ""}
                        onChange={onChange("seo_site_title")}
                        placeholder="Project Ruda Śląska - Mapa 1:1 do Euro Truck Simulator 2"
                        className="input"
                        maxLength={120}
                    />
                </Field>

                <Field label={`Meta description (≤ 160 znaków) · ${(data.seo_meta_description || "").length} znaków`}>
                    <textarea
                        data-testid="settings-seo-description"
                        value={data.seo_meta_description || ""}
                        onChange={onChange("seo_meta_description")}
                        rows={3}
                        maxLength={300}
                        className="input"
                    />
                </Field>

                <Field label="Słowa kluczowe (rozdzielone przecinkami)">
                    <input
                        data-testid="settings-seo-keywords"
                        value={data.seo_keywords || ""}
                        onChange={onChange("seo_keywords")}
                        placeholder="Project Ruda Śląska, ETS2, mapa, mod, Ruda Śląska, mapa 1:1"
                        className="input"
                    />
                </Field>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Obrazek Open Graph (URL lub plik · zalecane 1200×630)
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        {data.og_image_url && (
                            <img
                                src={
                                    data.og_image_url.startsWith("http")
                                        ? data.og_image_url
                                        : `${process.env.REACT_APP_BACKEND_URL}${data.og_image_url}`
                                }
                                alt="OG preview"
                                className="h-14 w-28 object-cover bg-[color:var(--bg-base)] border border-white/10 rounded-md"
                            />
                        )}
                        <input
                            data-testid="settings-seo-og-url"
                            value={data.og_image_url || ""}
                            onChange={onChange("og_image_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={ogImageFileRef}
                        data-testid="settings-seo-og-file"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                    />
                    <p className="text-[10px] text-[color:var(--text-muted)] mt-1">
                        Pokazywany przy udostępnieniu linku na Facebooku, X, Discordzie itp. Gdy puste - używane jest logo lub tło hero.
                    </p>
                </div>
            </div>

            <div className="panel-yellow p-6 lg:col-span-2 space-y-4">
                <div className="label-eyebrow flex items-center gap-2">
                    <MessageSquare size={14} /> Discord widget
                </div>
                <p className="text-xs text-[color:var(--text-muted)] -mt-2">
                    Wymagane: w ustawieniach swojego serwera Discord włącz
                    <strong> Ustawienia serwera → Widget → „Enable Server Widget"</strong>{" "}
                    oraz skopiuj Server ID (Server Settings → Widget → SERVER ID,
                    albo prawym przyciskiem na ikonę serwera → Copy Server ID po
                    włączeniu Trybu Dewelopera).
                </p>
                <Field label="Discord Server ID (Guild ID)">
                    <input
                        data-testid="settings-discord-guild-id"
                        value={data.discord_guild_id || ""}
                        onChange={onChange("discord_guild_id")}
                        placeholder="np. 1234567890123456789"
                        className="input"
                        inputMode="numeric"
                    />
                </Field>
                <p className="text-[10px] text-[color:var(--text-muted)]">
                    Po zapisaniu na stronie głównej (w sekcji Społeczność) pojawi
                    się żywy panel Discorda z liczbą osób online i przyciskiem
                    „Dołącz". Wyłączenie widgetu na Discordzie ukryje panel.
                </p>
            </div>

            <div
                data-testid="settings-maintenance-panel"
                className={`p-6 lg:col-span-2 space-y-4 rounded-xl border transition-colors ${
                    data.maintenance_mode
                        ? "border-[color:var(--primary)]/40 bg-[color:var(--primary)]/5"
                        : "border-white/10 bg-[color:var(--bg-surface)]"
                }`}
            >
                <div className="label-eyebrow flex items-center gap-2">
                    <Wrench size={14} /> Tryb serwisowy
                </div>
                <p className="text-xs text-[color:var(--text-muted)] -mt-2">
                    Po włączeniu użytkownicy zobaczą stronę z informacją o pracach
                    serwisowych oraz linkami do mediów społecznościowych. Ty (jako
                    zalogowany admin) nadal widzisz normalną stronę.
                </p>

                <label className="flex items-start gap-3 p-4 rounded-lg border border-white/10 bg-[color:var(--bg-base)] cursor-pointer select-none">
                    <input
                        type="checkbox"
                        data-testid="settings-maintenance-toggle"
                        checked={!!data.maintenance_mode}
                        onChange={(e) =>
                            setData((prev) => ({ ...prev, maintenance_mode: e.target.checked }))
                        }
                        className="mt-1 h-4 w-4 accent-[color:var(--primary)] shrink-0"
                    />
                    <span className="flex-1">
                        <span className="block text-sm font-medium text-white">
                            Włącz tryb serwisowy
                        </span>
                        <span className="block text-xs text-[color:var(--text-secondary)] mt-1">
                            Publiczna strona zostanie zastąpiona ekranem z informacją o
                            pracach serwisowych. Administratorzy nadal mają pełny dostęp.
                        </span>
                    </span>
                </label>

                {data.maintenance_mode && (
                    <div
                        data-testid="settings-maintenance-warning"
                        className="flex items-start gap-3 p-3 rounded-md border border-amber-400/30 bg-amber-400/10 text-amber-100"
                    >
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <div className="text-xs leading-relaxed">
                            Tryb serwisowy jest <strong>aktywny</strong>. Wszyscy niezalogowani
                            odwiedzający widzą stronę informacyjną. Ponowne wejście publiczne
                            odblokujesz odznaczając powyższy checkbox i klikając „Zapisz ustawienia&rdquo;.
                        </div>
                    </div>
                )}

                <Field label="Nagłówek">
                    <input
                        data-testid="settings-maintenance-title"
                        value={data.maintenance_title || ""}
                        onChange={onChange("maintenance_title")}
                        placeholder="Prace serwisowe"
                        className="input"
                        maxLength={120}
                    />
                </Field>

                <Field label="Wiadomość dla użytkowników">
                    <textarea
                        data-testid="settings-maintenance-message"
                        value={data.maintenance_message || ""}
                        onChange={onChange("maintenance_message")}
                        rows={4}
                        className="input"
                        placeholder="Aktualnie prowadzimy prace nad stroną. Zapraszamy do naszych mediów społecznościowych - wrócimy wkrótce!"
                    />
                </Field>

                <Field label="Przewidywany powrót (opcjonalnie)">
                    <input
                        data-testid="settings-maintenance-eta"
                        value={data.maintenance_eta || ""}
                        onChange={onChange("maintenance_eta")}
                        placeholder="np. dziś do 21:00 · jutro rano · 15 sierpnia"
                        className="input"
                        maxLength={140}
                    />
                </Field>

                <p className="text-[10px] text-[color:var(--text-muted)]">
                    Linki do social mediów pojawiają się automatycznie na stronie
                    serwisowej - zarządzasz nimi w zakładce „Społeczność&rdquo;.
                </p>
            </div>

            <div className="panel-yellow p-6 lg:col-span-2 text-sm text-[color:var(--text-secondary)] flex items-center gap-3">
                <Puzzle size={18} className="text-[#34d399] shrink-0" />
                <span>
                    Dodatki (np. Project Gmina Brańszczyk) zarządzasz teraz w osobnej
                    zakładce <strong className="text-white">{"„Dodatki"}</strong> - z
                    własnym opisem, wersją, plikiem i obrazkiem.
                </span>
            </div>
            <style>{`
                .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                .input:focus { outline:none; border-color: var(--primary); }
            `}</style>
        </form>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
